import { Form, InputNumber, Select, Tag } from "antd";

/**
 * "Hiển thị cho ai" — dùng chung cho Banner, Ô tiện ích, Trang HTML và Bài viết.
 * Giá trị phải khớp backend services/audience-rules.js, nếu lệch backend sẽ tự
 * đưa về 'all' (hiện cho tất cả) và admin tưởng đã nhắm đúng đối tượng.
 */
export const AUDIENCE_TAGS = {
    all: { text: "Tất cả", color: "default" },
    guest: { text: "Chưa đăng nhập", color: "purple" },
    logged_in: { text: "Đã đăng nhập", color: "geekblue" },
    new_user: { text: "User mới", color: "gold" },
    no_order: { text: "Chưa có đơn", color: "volcano" },
    has_order: { text: "Đã có đơn", color: "cyan" },
};

/** Nhãn đầy đủ trong ô chọn (dài hơn tag để admin không hiểu nhầm). */
const AUDIENCE_OPTIONS = [
    { value: "all", label: "Tất cả" },
    { value: "guest", label: "Khách chưa đăng nhập" },
    { value: "logged_in", label: "Đã đăng nhập" },
    { value: "new_user", label: "User mới" },
    { value: "no_order", label: "Chưa có đơn nào" },
    { value: "has_order", label: "Đã có đơn" },
];

/** Tag hiển thị trong bảng danh sách ("User mới 30n" = trong 30 ngày đầu). */
export function AudienceTag({ value, days }) {
    const info = AUDIENCE_TAGS[value] || AUDIENCE_TAGS.all;
    const suffix = value === "new_user" ? ` ${days || 7}n` : "";
    return <Tag color={info.color}>{`${info.text}${suffix}`}</Tag>;
}

/** Cột "Đối tượng" dùng chung cho các bảng danh sách (banner, tiện ích, trang, bài viết). */
export const audienceColumn = (overrides = {}) => ({
    title: "Đối tượng",
    dataIndex: "audience",
    key: "audience",
    width: 120,
    render: (value, record) => <AudienceTag value={value} days={record.audienceDays} />,
    ...overrides,
});

/**
 * Cặp field: chọn đối tượng + số ngày (chỉ hiện khi chọn "User mới").
 * @param {string} extra ghi chú dưới ô chọn (tuỳ mục mà cách hoạt động khác nhau)
 */
export function AudienceFields({ extra }) {
    return (
        <div className="flex gap-4">
            <Form.Item
                label="Hiển thị cho"
                name="audience"
                className="w-1/2"
                extra={
                    extra ||
                    "Lọc theo người xem. Khách chưa đăng nhập chỉ thấy 'Tất cả' và 'Khách chưa đăng nhập'."
                }
            >
                <Select options={AUDIENCE_OPTIONS} />
            </Form.Item>
            {/* Ô số ngày chỉ hiện với "user mới" — các loại khác không dùng tới */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.audience !== cur.audience}>
                {({ getFieldValue }) =>
                    getFieldValue("audience") === "new_user" ? (
                        <Form.Item
                            label="Trong bao nhiêu ngày"
                            name="audienceDays"
                            className="w-1/2"
                            extra="Tính từ lúc tạo tài khoản. Để trống = 7 ngày."
                        >
                            <InputNumber min={1} max={365} className="w-full" placeholder="7" />
                        </Form.Item>
                    ) : null
                }
            </Form.Item>
        </div>
    );
}

/**
 * Bản KHÔNG dùng antd Form (màn nào quản state bằng useState, vd Trang HTML).
 * @param {string} value audience hiện tại
 * @param {number|null} days audienceDays hiện tại
 */
export function AudienceSelect({ value, days, onChange, onDaysChange, extra, className = "" }) {
    return (
        <div className={className}>
            <div className="text-sm font-medium mb-1">Hiển thị cho</div>
            <div className="flex gap-3">
                <Select
                    value={value || "all"}
                    onChange={onChange}
                    options={AUDIENCE_OPTIONS}
                    className="flex-1"
                />
                {value === "new_user" && (
                    <InputNumber
                        min={1}
                        max={365}
                        value={days ?? null}
                        onChange={onDaysChange}
                        placeholder="7 ngày"
                        className="w-32"
                    />
                )}
            </div>
            {extra && <div className="text-xs text-content-3 mt-1">{extra}</div>}
        </div>
    );
}

/**
 * Giá trị gửi lên backend. Số ngày chỉ có nghĩa với "user mới" — loại khác gửi rỗng
 * để backend xoá giá trị cũ.
 */
export function audiencePayload(values) {
    return {
        audience: values.audience || "all",
        audienceDays:
            values.audience === "new_user" && values.audienceDays
                ? String(values.audienceDays)
                : "",
    };
}
